import { create } from "zustand"
import { getPermissionsByRole, Permissions } from "shared"

import type { AuthSession } from "@/lib/api/auth"
import {
  getOtkSubject,
  type ConnectSubjectResult,
  type OtkSubjectProfile,
} from "@/lib/api/subjects"

interface SessionState {
  user: AuthSession["user"] | null
  // `GET /api/otk/subject` (lib/api/subjects.ts's `getOtkSubject`), fetched
  // once `user.subjectId` is known — see `RequireAuth`. `null` both before
  // that fetch resolves and for a user with no `subjectId` yet.
  subject: OtkSubjectProfile | null
  // Result of login.md endpoint 5 (`connect`) — kept separate from `subject`
  // since it comes back from a different endpoint with a different (smaller)
  // shape, not the full `GET /subject` profile.
  connectedSubject: ConnectSubjectResult | null
  setSession: (session: AuthSession) => void
  setSubject: (subject: OtkSubjectProfile | null) => void
  // Wraps `getOtkSubject()` and stores the result, so any page can trigger
  // the fetch (and read/await its result) without duplicating the call or
  // reaching into `lib/api/subjects` directly — `RequireAuth` and
  // `SubjectProfile` both call this rather than fetching independently.
  // Rejects like the raw call; callers still handle their own loading/error
  // UI on top of it.
  fetchSubject: () => Promise<OtkSubjectProfile>
  setConnectedSubject: (result: ConnectSubjectResult) => void
  clear: () => void
}

export const useSession = create<SessionState>((set) => ({
  user: null,
  subject: null,
  connectedSubject: null,
  setSession: (session) => set({ user: session.user }),
  setSubject: (subject) => set({ subject }),
  fetchSubject: async () => {
    const subject = await getOtkSubject()
    set({ subject })
    return subject
  },
  setConnectedSubject: (result) => set({ connectedSubject: result }),
  clear: () => set({ user: null, subject: null, connectedSubject: null }),
}))

// `AppShell`'s `SubjectStatusIndicator` treats an excluded subject as the
// application-wide read-only signal, so pages reuse the same subject field.
export const useReadOnly = () =>
  useSession((state) => state.subject?.status === "ВИКЛЮЧЕНИЙ")

export const useIsAdmin = () =>
  useSession((state) => state.user?.roles.includes("ADMIN") ?? false)

function useHasPermission(permission: (typeof Permissions)[keyof typeof Permissions]) {
  const roles = useSession((state) => state.user?.roles ?? [])
  return roles.some((role) => getPermissionsByRole(role).includes(permission))
}

// warehouse.md's `Право на процедуру знищення` — `manageWarehouseDistribution`
// (per `shared`'s `ROLE_PERMISSIONS`, granted to ADMIN only today, same as
// `manageUsersRolesInvites` above), plus `signAndSendEdrtz` (documents.md's
// annul gate — warehouse.md's «Використані» row action defers to the same
// centralized check). Derived from the session's own `roles` rather than
// hardcoded to `isAdmin`, so a future role grant in `shared` takes effect
// here without another edit.
export function useRolePermissions() {
  const isAdmin = useIsAdmin()
  const manageWarehouseDistribution = useHasPermission(Permissions.MANAGE_WAREHOUSE_DISTRIBUTION)
  const signAndSendEdrtz = useHasPermission(Permissions.SIGN_AND_SEND_EDRTZ)
  return { manageUsersRolesInvites: isAdmin, manageWarehouseDistribution, signAndSendEdrtz }
}
